/** Contexte d'une connexion SSO en cours, conservé le temps de l'aller-retour Keycloak. */
const SSO_FLOW_KEY = 'sso_flow';

/**
 * Horodatages des derniers départs automatiques vers Keycloak. Survit au nettoyage
 * de session (voir StorageService.clean) : c'est justement ce nettoyage qui ramène sur /login.
 */
export const SSO_REDIRECTS_KEY = 'sso_redirects';
const LOOP_WINDOW_MS = 60_000;
const MAX_REDIRECTS_IN_WINDOW = 2;

interface SsoFlow {
  state: string;
  codeVerifier: string;
}

interface SsoLoginConfig {
  ssoUrl: string;
  realm: string;
  clientId: string;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function randomToken(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

/** Challenge PKCE S256 : SHA-256 du verifier, encodé en base64url. */
export async function computeCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return base64Url(new Uint8Array(digest));
}

/** URL d'autorisation Keycloak, avec un state et un challenge PKCE neufs. */
export async function buildSsoLoginUrl(config: SsoLoginConfig, redirectUri: string): Promise<string> {
  const flow: SsoFlow = { state: randomToken(), codeVerifier: randomToken() };
  sessionStorage.setItem(SSO_FLOW_KEY, JSON.stringify(flow));

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    state: flow.state,
    code_challenge: await computeCodeChallenge(flow.codeVerifier),
    code_challenge_method: 'S256',
  });
  return `${config.ssoUrl}/realms/${config.realm}/protocol/openid-connect/auth?${params}`;
}

/** Verifier PKCE si le state reçu est celui émis par cet onglet, sinon null. Usage unique. */
export function consumeSsoFlow(state: string | null | undefined): string | null {
  const raw = sessionStorage.getItem(SSO_FLOW_KEY);
  sessionStorage.removeItem(SSO_FLOW_KEY);
  if (!raw || !state) {
    return null;
  }

  try {
    const flow: SsoFlow = JSON.parse(raw);
    return flow.state === state ? flow.codeVerifier : null;
  } catch {
    return null;
  }
}

function recentSsoRedirects(now: number): number[] {
  try {
    const stamps: unknown = JSON.parse(sessionStorage.getItem(SSO_REDIRECTS_KEY) ?? '[]');
    return Array.isArray(stamps) ? stamps.filter((t): t is number => typeof t === 'number' && now - t < LOOP_WINDOW_MS) : [];
  } catch {
    return [];
  }
}

/**
 * Vrai si l'app vient déjà de partir plusieurs fois vers Keycloak en peu de temps :
 * signe d'une boucle (token obtenu puis refusé par l'API), qu'on coupe en ne redirigeant plus automatiquement.
 */
export function isSsoRedirectLooping(now = Date.now()): boolean {
  return recentSsoRedirects(now).length >= MAX_REDIRECTS_IN_WINDOW;
}

export function recordSsoRedirect(now = Date.now()): void {
  sessionStorage.setItem(SSO_REDIRECTS_KEY, JSON.stringify([...recentSsoRedirects(now), now]));
}

/** Une déconnexion volontaire n'est pas une boucle : le prochain départ vers Keycloak repart de zéro. */
export function resetSsoRedirects(): void {
  sessionStorage.removeItem(SSO_REDIRECTS_KEY);
}
